import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { chatAPI } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  Send,
  ArrowLeft,
  Loader2,
  Check,
  CheckCheck,
  Wifi,
  WifiOff
} from 'lucide-react';
import { toast } from 'sonner';

// Conversations List Component
export const ConversationsList = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineStatuses, setOnlineStatuses] = useState({});

  useEffect(() => {
    loadConversations();
  }, []);

  // Check online status for all conversations
  useEffect(() => {
    const checkOnlineStatuses = async () => {
      const statuses = {};
      for (const conv of conversations) {
        const otherId = user?.role === 'patient' ? conv.professional_id : conv.patient_id;
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/ws/online/${otherId}`
          );
          const data = await response.json();
          statuses[otherId] = data.is_online;
        } catch (e) {
          statuses[otherId] = false;
        }
      }
      setOnlineStatuses(statuses);
    };

    if (conversations.length > 0) {
      checkOnlineStatuses();
      // Refresh every 30 seconds
      const interval = setInterval(checkOnlineStatuses, 30000);
      return () => clearInterval(interval);
    }
  }, [conversations, user]);

  const loadConversations = async () => {
    try {
      const response = await chatAPI.getConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'maintenant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="conversations-list">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Messages
        </h1>
        <p className="text-[#64748B] mt-1">
          {user?.role === 'patient' 
            ? 'Discutez avec vos professionnels de santé'
            : 'Discutez avec vos patients'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
        </div>
      ) : conversations.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <MessageCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune conversation</h3>
            <p className="text-slate-500 mt-2">
              {user?.role === 'patient' 
                ? 'Vos conversations avec les médecins apparaîtront ici'
                : 'Vos conversations avec les patients apparaîtront ici'}
            </p>
            {user?.role === 'patient' && (
              <Button
                onClick={() => navigate('/search')}
                className="mt-4 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl"
                data-testid="find-doctor-btn"
              >
                Trouver un médecin
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => {
            const otherId = user?.role === 'patient' ? conv.professional_id : conv.patient_id;
            const otherName = user?.role === 'patient' ? conv.professional_name : conv.patient_name;
            const hasUnread = conv.unread_count > 0;
            const isOnline = onlineStatuses[otherId] || false;
        
            return (
              <Card 
                key={conv.id}
                className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${hasUnread ? 'bg-blue-50/50' : ''}`}
                onClick={() => navigate(`/chat/${conv.id}`)}
                data-testid={`conversation-${conv.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-[#E53935]/10 text-[#E53935]">
                          {otherName?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      {/* Online indicator */}
                      <span 
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                          isOnline ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                        title={isOnline ? 'En ligne' : 'Hors ligne'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-semibold truncate ${hasUnread ? 'text-slate-900' : 'text-slate-700'}`}>
                          {user?.role === 'patient' ? `Dr. ${otherName}` : otherName}
                        </h3>
                        <span className="text-xs text-slate-400">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className={`text-sm truncate ${hasUnread ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                          {conv.last_message || 'Nouvelle conversation'}
                        </p>
                        {hasUnread && (
                          <Badge className="bg-[#E53935] text-white text-xs px-2 py-0 min-w-[20px] text-center">
                            {conv.unread_count}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Chat Room Component with WebSocket
const ChatRoom = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [otherUserOnline, setOtherUserOnline] = useState(false);

  // Get the other user's ID
  const getOtherUserId = useCallback(() => {
    if (!conversation) return null;
    return user?.role === 'patient' 
      ? conversation.professional_id 
      : conversation.patient_id;
  }, [conversation, user]);

  // Handle incoming WebSocket messages
  const handleWsMessage = useCallback((message) => {
    setMessages(prev => {
      // Avoid duplicates
      if (prev.some(m => m.id === message.id)) return prev;
      return [...prev, message];
    });
  }, []);

  // Handle presence updates
  const handlePresence = useCallback((userId, isOnline) => {
    const otherId = getOtherUserId();
    if (userId === otherId) {
      setOtherUserOnline(isOnline);
    }
  }, [getOtherUserId]);

  // Handle typing indicators
  const handleTyping = useCallback((userId, isTyping) => {
    const otherId = getOtherUserId();
    if (userId === otherId) {
      setOtherUserTyping(isTyping);
    }
  }, [getOtherUserId]);

  // Initialize WebSocket
  const {
    isConnected,
    sendMessage: wsSendMessage,
    sendTyping,
    sendReadReceipt,
    isUserOnline
  } = useWebSocket(
    conversationId,
    token,
    handleWsMessage,
    handlePresence,
    handleTyping
  );

  // Load initial chat data
  useEffect(() => {
    loadChat();
  }, [conversationId]);

  // Update online status when WebSocket connects
  useEffect(() => {
    if (isConnected && conversation) {
      const otherId = getOtherUserId();
      setOtherUserOnline(isUserOnline(otherId));
      // Mark messages as read
      sendReadReceipt();
    }
  }, [isConnected, conversation, getOtherUserId, isUserOnline, sendReadReceipt]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChat = async () => {
    try {
      const [convRes, messagesRes] = await Promise.all([
        chatAPI.getConversation(conversationId),
        chatAPI.getMessages(conversationId)
      ]);
      setConversation(convRes.data.conversation);
      setMessages(messagesRes.data.messages || []);
    } catch (error) {
      console.error('Error loading chat:', error);
      toast.error('Conversation non trouvée');
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (isConnected) {
      sendTyping(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(false);
      }, 2000);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    const content = newMessage.trim();
    setNewMessage('');
    
    // Clear typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTyping(false);
    
    // Try WebSocket first
    if (isConnected && wsSendMessage(content)) {
      // Message sent via WebSocket, it will come back in handleWsMessage
      inputRef.current?.focus();
      return;
    }
    
    // Fallback to REST API
    setSending(true);
    try {
      const response = await chatAPI.sendMessage(conversationId, content);
      setMessages(prev => [...prev, response.data.message]);
      inputRef.current?.focus();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
      setNewMessage(content); // Restore message
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Aujourd\'hui';
    if (date.toDateString() === yesterday.toDateString()) return 'Hier';
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  const otherName = user?.role === 'patient' 
    ? conversation?.professional_name 
    : conversation?.patient_name;

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-slate-50" data-testid="chat-room">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/chat')}
          className="md:hidden"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="relative">
          <Avatar className="w-10 h-10">
            <AvatarFallback className="bg-[#E53935]/10 text-[#E53935]">
              {otherName?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          {/* Online indicator */}
          <span 
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
              otherUserOnline ? 'bg-green-500' : 'bg-slate-300'
            }`}
          />
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-slate-900">
            {user?.role === 'patient' ? `Dr. ${otherName}` : otherName}
          </h2>
          <div className="flex items-center gap-2">
            <p className="text-xs text-slate-500">
              {otherUserTyping 
                ? 'En train d\'écrire...'
                : otherUserOnline 
                  ? 'En ligne' 
                  : 'Hors ligne'}
            </p>
          </div>
        </div>
        {/* Connection status */}
        <div className={`flex items-center gap-1 text-xs ${isConnected ? 'text-green-600' : 'text-slate-400'}`}>
          {isConnected ? (
            <>
              <Wifi className="w-4 h-4" />
              <span className="hidden sm:inline">Temps réel</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4" />
              <span className="hidden sm:inline">Hors ligne</span>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date separator */}
            <div className="flex items-center justify-center my-4">
              <span className="px-3 py-1 bg-slate-200 rounded-full text-xs text-slate-600">
                {formatDate(dateMessages[0].created_at)}
              </span>
            </div>
            
            {/* Messages for this date */}
            {dateMessages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                      isOwn
                        ? 'bg-[#E53935] text-white rounded-br-md'
                        : 'bg-white shadow-sm rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 ${
                      isOwn ? 'text-white/70' : 'text-slate-400'
                    }`}>
                      <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                      {isOwn && (
                        msg.read 
                          ? <CheckCheck className="w-3 h-3" />
                          : <Check className="w-3 h-3" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        
        {/* Typing indicator */}
        {otherUserTyping && (
          <div className="flex justify-start mb-2">
            <div className="bg-white shadow-sm rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="bg-white border-t p-4">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Écrivez votre message..."
            className="flex-1 rounded-full"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="rounded-full w-10 h-10 p-0 bg-[#E53935] hover:bg-[#D32F2F]"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChatRoom;
